import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { Orden } from "@/componentes/ordenes/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

type Proveedor = { id: number; nombre: string; rif: string | null }
type Categoria = { id: number; nombre: string; activo: boolean }
type Divisa = { id: number; codigo: string; nombre: string }

/**
 * Los datos de la cabecera de una orden, editables mientras es borrador.
 *
 * ⚠ **En `BORRADOR` se edita todo; después, sólo el documento y sólo el
 * superadministrador** (`orden_pago:editar_aprobada`). Lo que mueve el importe
 * —moneda, líneas, impuestos— es lo que alguien autorizó, y cambiarlo después sería
 * firmar por él: para eso se rechaza la orden y se corrige en borrador. Lo que sí se
 * corrige es una errata en el número de control, que si no obliga a repetir el
 * circuito entero con sus dos firmas — y entonces el número se queda mal.
 *
 * ⚠ **Cambiar de proveedor puede chocar con las líneas.** Si alguna cuelga de un
 * gasto del catálogo del proveedor anterior, el servidor lo rechaza con un 409 —«ese
 * gasto es de otro proveedor»— en vez de dejar la orden en un estado que el informe
 * por concepto no sabría agrupar. El aviso se lee aquí.
 *
 * ⚠ **El número de control lleva el formato del SENIAT** (`00-0000000`) y lo valida
 * el servidor: es lo que pide el libro de compras, y uno mal escrito no se descubre
 * hasta que hay que declarar.
 */
export function DatosOrden({
  orden,
  soloDocumento = false,
  onCerrar,
}: {
  orden: Orden
  /**
   * Con la orden ya aprobada: sólo los datos del documento.
   *
   * ⚠ **La moneda no está, y el servidor la rechaza igualmente.** Cambiarla movería
   * lo que tesorería va a debitar, y el desglose se congeló al aprobar: quien firmó
   * autorizó una cifra. Para eso se rechaza la orden y se corrige en borrador.
   */
  soloDocumento?: boolean
  onCerrar: () => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const [proveedorId, setProveedorId] = useState<number | null>(orden.proveedor_id)
  const [categoriaId, setCategoriaId] = useState<number | null>(orden.categoria_id)
  const [divisaId, setDivisaId] = useState<number | null>(orden.divisa_id)
  const [concepto, setConcepto] = useState(orden.concepto)

  const proveedores = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO"),
    enabled: tienePermiso(usuario, "proveedor:ver"),
  })
  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<Categoria[]>("/api/v1/categorias-gasto"),
    enabled: tienePermiso(usuario, "gasto:ver"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: tienePermiso(usuario, "tesoreria:ver"),
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.patch<Orden>(`/api/v1/ordenes-pago/${orden.id}`, cuerpo),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["orden-pago", orden.id] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      onCerrar()
    },
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const texto = (campo: string) => {
      const valor = String(datos.get(campo) ?? "").trim()
      return valor === "" ? null : valor
    }
    guardar.mutate({
      concepto: concepto.trim(),
      proveedor_id: proveedorId,
      categoria_id: categoriaId,
      // Con la orden aprobada ni se manda: el servidor la rechazaría entera.
      ...(soloDocumento ? {} : { divisa_id: divisaId }),
      numero_documento: texto("numero_documento"),
      numero_control: texto("numero_control"),
      fecha_emision: texto("fecha_emision"),
      fecha_vencimiento: texto("fecha_vencimiento"),
      fecha_requerida: texto("fecha_requerida"),
      notas: texto("notas"),
      sin_documento_fiscal: datos.get("sin_documento_fiscal") === "on",
    })
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="tarjeta space-y-3 p-3 text-sm" onSubmit={enviar}>
      {soloDocumento && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          La orden ya está aprobada: aquí se corrigen los datos del documento. Los montos, la moneda
          y los impuestos son lo que alguien autorizó — para cambiarlos hay que rechazarla y
          corregirla en borrador.
        </p>
      )}
      <Campo
        etiqueta="Descripción"
        ayuda="Con qué se reconoce en el listado: «Factura N.º 5615616». Lo que se compró son los conceptos."
      >
        <input
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          required
          minLength={3}
          maxLength={200}
          aria-label="Descripción de la orden"
          className="campo"
        />
      </Campo>

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo
          etiqueta="Proveedor"
          ayuda="Sólo administrativos: lo operativo va por el expediente."
        >
          <SelectorBuscable
            id="orden-editar-proveedor"
            opciones={(proveedores.data ?? []).map((p) => ({
              valor: p.id,
              etiqueta: p.nombre,
              detalle: p.rif ?? undefined,
            }))}
            valor={proveedorId}
            onCambiar={setProveedorId}
            placeholder="Nombre o RIF…"
          />
        </Campo>
        <Campo etiqueta="Categoría">
          <SelectorBuscable
            id="orden-editar-categoria"
            opciones={(categorias.data ?? [])
              .filter((c) => c.activo || c.id === categoriaId)
              .map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
            valor={categoriaId}
            onCambiar={setCategoriaId}
            vacio="Sin categoría"
          />
        </Campo>
        {!soloDocumento && (
          <Campo etiqueta="Moneda de cobro">
            <SelectorBuscable
              id="orden-editar-divisa"
              opciones={(divisas.data ?? []).map((d) => ({
                valor: d.id,
                etiqueta: d.codigo,
                detalle: d.nombre,
              }))}
              valor={divisaId}
              onCambiar={setDivisaId}
            />
          </Campo>
        )}
        <Campo etiqueta="Fecha requerida">
          <input
            name="fecha_requerida"
            type="date"
            defaultValue={orden.fecha_requerida ?? ""}
            aria-label="Fecha requerida"
            className="campo"
          />
        </Campo>
      </div>

      {/* ⚠ Sin documento fiscal: una solicitud de fondos sin factura. Fuera del libro
          de compras y de sus pendientes, donde se quedaría «sin número de factura». */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          name="sin_documento_fiscal"
          defaultChecked={orden.sin_documento_fiscal ?? false}
        />
        Sin documento fiscal (solicitud de fondos): no entra al libro de compras
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="N.º de factura">
          <input
            name="numero_documento"
            maxLength={60}
            defaultValue={orden.numero_documento ?? ""}
            aria-label="Número de factura"
            className="campo"
          />
        </Campo>
        <Campo etiqueta="N.º de control" ayuda="Formato del SENIAT: 00-0000000.">
          <input
            name="numero_control"
            maxLength={20}
            defaultValue={orden.numero_control ?? ""}
            aria-label="Número de control"
            className="campo"
          />
        </Campo>
        <Campo etiqueta="Fecha de emisión">
          <input
            name="fecha_emision"
            type="date"
            defaultValue={orden.fecha_emision ?? ""}
            aria-label="Fecha de emisión de la factura"
            className="campo"
          />
        </Campo>
        <Campo etiqueta="Fecha de vencimiento">
          <input
            name="fecha_vencimiento"
            type="date"
            defaultValue={orden.fecha_vencimiento ?? ""}
            aria-label="Fecha de vencimiento"
            className="campo"
          />
        </Campo>
      </div>

      <Campo etiqueta="Notas">
        <input
          name="notas"
          maxLength={500}
          defaultValue={orden.notas ?? ""}
          aria-label="Notas de la orden"
          className="campo"
        />
      </Campo>

      {error && <p style={{ color: "var(--destructive)" }}>{error}</p>}

      <div className="flex justify-end gap-2">
        <Boton tamano="sm" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton type="submit" tamano="sm" variante="primario" disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </form>
  )
}

/** Etiqueta y control. La etiqueta envuelve al campo: aquí no hay `id` que repetir. */
function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string
  ayuda?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <span className="block font-medium">{etiqueta}</span>
      {children}
      {ayuda && (
        <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </span>
      )}
    </div>
  )
}
